import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/useAuth';
import { useSocket } from '../../../context/SocketProvider';
import {
  getDiscussionInfoApi,
  getDiscussionMessagesApi,
  postDiscussionMessageApi,
  deleteDiscussionMessageApi,
  pinDiscussionMessageApi,
  toggleDiscussionApi,
  toggleDiscussionLockApi,
  muteDiscussionParticipantApi,
  unmuteDiscussionParticipantApi,
} from '../../../api/apiServices';

function initialsFor(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || name[0].toUpperCase();
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `${days}d`;
}

function Toggle({ checked, onChange, disabled, label, hint }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-50' : 'cursor-pointer'}`}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5.5 h-6 rounded-full transition-colors flex-shrink-0 mt-0.5 cursor-pointer disabled:cursor-not-allowed ${
          checked ? 'bg-teal-600' : 'bg-slate-300'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
      <div>
        <p className="text-xs font-bold text-slate-800">{label}</p>
        {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
      </div>
    </label>
  );
}

/**
 * Per-course group discussion room. Scoped to enrolled students + the
 * instructor of `courseId` (admin/superadmin can moderate any course).
 * The instructor gets a moderation toolbar (enable/disable, lock/unlock,
 * pin/delete/mute) on top of the same message thread everyone else sees.
 */
export default function DiscussionRoom({ courseId }) {
  const { user } = useAuth();
  const socket = useSocket();
  const currentUserId = user?.userId || user?.id || user?._id;

  const [info, setInfo] = useState(null); // { enabled, locked, isManager, isEnrolled, isMuted, mutedUserIds }
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState(''); // which moderation action is in flight
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const infoRes = await getDiscussionInfoApi(courseId);
      if (infoRes?.status) setInfo(infoRes.data);

      if (infoRes?.status && (infoRes.data.isManager || infoRes.data.isEnrolled)) {
        const msgRes = await getDiscussionMessagesApi(courseId);
        if (msgRes?.status) setMessages(msgRes.data || []);
      }
    } catch (err) {
      console.error('Failed to load discussion room', err);
      setError(err.response?.data?.message || 'Could not load the discussion room.');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Live updates over the socket
  useEffect(() => {
    if (!socket || !courseId) return;

    socket.emit('joinDiscussion', { courseId });

    const onMessage = (msg) => {
      if (String(msg.courseId) !== String(courseId)) return;
      setMessages((prev) => (prev.some((m) => m._id === msg._id) ? prev : [...prev, msg]));
    };
    const onDeleted = ({ _id }) => {
      setMessages((prev) => prev.filter((m) => m._id !== _id));
    };
    const onPinned = ({ _id, pinned }) => {
      setMessages((prev) => prev.map((m) => (m._id === _id ? { ...m, pinned } : m)));
    };
    const onLockChanged = ({ locked }) => {
      setInfo((prev) => (prev ? { ...prev, locked } : prev));
    };
    const onMuted = ({ userId, muted }) => {
      if (userId === currentUserId) {
        setInfo((prev) => (prev ? { ...prev, isMuted: muted } : prev));
      }
      setInfo((prev) => {
        if (!prev?.mutedUserIds) return prev;
        const set = new Set(prev.mutedUserIds);
        if (muted) set.add(userId); else set.delete(userId);
        return { ...prev, mutedUserIds: Array.from(set) };
      });
    };

    socket.on('discussionMessage', onMessage);
    socket.on('discussionMessageDeleted', onDeleted);
    socket.on('discussionMessagePinned', onPinned);
    socket.on('discussionLockChanged', onLockChanged);
    socket.on('discussionParticipantMuted', onMuted);

    return () => {
      socket.emit('leaveDiscussion', { courseId });
      socket.off('discussionMessage', onMessage);
      socket.off('discussionMessageDeleted', onDeleted);
      socket.off('discussionMessagePinned', onPinned);
      socket.off('discussionLockChanged', onLockChanged);
      socket.off('discussionParticipantMuted', onMuted);
    };
  }, [socket, courseId, currentUserId]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;

    setSending(true);
    setDraft('');
    setError('');
    try {
      const res = await postDiscussionMessageApi(courseId, text);
      if (res?.status && res.data) {
        setMessages((prev) => (prev.some((m) => m._id === res.data._id) ? prev : [...prev, res.data]));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Message failed to send.');
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  const handleToggleEnabled = async (enabled) => {
    setBusyAction('enabled');
    try {
      await toggleDiscussionApi(courseId, enabled);
      setInfo((prev) => (prev ? { ...prev, enabled } : prev));
      if (enabled) load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the discussion room.');
    } finally {
      setBusyAction('');
    }
  };

  const handleToggleLock = async (locked) => {
    setBusyAction('locked');
    try {
      await toggleDiscussionLockApi(courseId, locked);
      setInfo((prev) => (prev ? { ...prev, locked } : prev));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the room lock.');
    } finally {
      setBusyAction('');
    }
  };

  const handleDelete = async (messageId) => {
    try {
      await deleteDiscussionMessageApi(courseId, messageId);
      setMessages((prev) => prev.filter((m) => m._id !== messageId));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not remove that message.');
    }
  };

  const handlePin = async (messageId, pinned) => {
    try {
      await pinDiscussionMessageApi(courseId, messageId, pinned);
      setMessages((prev) => prev.map((m) => (m._id === messageId ? { ...m, pinned } : m)));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update the pinned message.');
    }
  };

  const handleMuteToggle = async (userId, muted) => {
    try {
      if (muted) await muteDiscussionParticipantApi(courseId, userId);
      else await unmuteDiscussionParticipantApi(courseId, userId);
      setInfo((prev) => {
        if (!prev) return prev;
        const set = new Set(prev.mutedUserIds || []);
        if (muted) set.add(userId); else set.delete(userId);
        return { ...prev, mutedUserIds: Array.from(set) };
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update that participant.');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-slate-400 text-center">Loading discussion room...</div>;
  }

  if (!info || (!info.isManager && !info.isEnrolled)) {
    // `error` is only set when the info request actually failed (a network
    // error, a 404, a 500, etc) -- as opposed to a clean 403 response that
    // simply means "you're not enrolled". Previously both cases rendered
    // the exact same "Enroll to join" copy, which hid a real backend error
    // (wrong course id, server exception, stale deploy, ...) behind a
    // message that looked like an ordinary permissions wall.
    return (
      <div className="p-8 text-center bg-white rounded-2xl border border-slate-100">
        <i className={`ri-${error ? 'error-warning-line' : 'group-line'} text-3xl ${error ? 'text-rose-300' : 'text-slate-300'} mb-2 block`} />
        <p className="text-sm font-semibold text-slate-700">
          {error ? "Couldn't load the discussion room" : 'Enroll to join the discussion'}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {error || "Only enrolled students and the instructor can access this course's discussion room."}
        </p>
      </div>
    );
  }

  const pinnedMessage = messages.find((m) => m.pinned);
  const isLockedForMe = info.locked && !info.isManager;
  const isMutedForMe = info.isMuted && !info.isManager;
  const canPost = info.enabled && !isLockedForMe && !isMutedForMe;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden flex flex-col" style={{ height: '32rem' }}>
      {info.isManager && (
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/60 flex flex-wrap items-center gap-6">
          <Toggle
            checked={Boolean(info.enabled)}
            onChange={handleToggleEnabled}
            disabled={busyAction === 'enabled'}
            label={info.enabled ? 'Discussion room is on' : 'Discussion room is off'}
            hint="Students can only see or post here while this is on"
          />
          <Toggle
            checked={Boolean(info.locked)}
            onChange={handleToggleLock}
            disabled={busyAction === 'locked' || !info.enabled}
            label={info.locked ? 'Locked (read-only for students)' : 'Open (everyone can post)'}
            hint="Lock it to make announcements without replies"
          />
        </div>
      )}

      {error && (
        <div className="px-4 py-2 bg-rose-50 border-b border-rose-100 text-rose-600 text-xs">{error}</div>
      )}

      {!info.enabled ? (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div>
            <i className="ri-chat-off-line text-3xl text-slate-300 mb-2 block" />
            <p className="text-sm font-semibold text-slate-600">
              {info.isManager ? "This course's discussion room is off" : "The discussion room isn't open yet"}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {info.isManager
                ? 'Turn it on above so your students can start discussing this course.'
                : "Check back once your instructor turns it on."}
            </p>
          </div>
        </div>
      ) : (
        <>
          {pinnedMessage && (
            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-start gap-2">
              <i className="ri-pushpin-fill text-amber-500 text-sm mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-bold text-amber-700">{pinnedMessage.senderName}</p>
                <p className="text-xs text-amber-800 truncate">{pinnedMessage.message}</p>
              </div>
              {info.isManager && (
                <button
                  onClick={() => handlePin(pinnedMessage._id, false)}
                  className="text-[11px] text-amber-500 hover:text-amber-700 cursor-pointer flex-shrink-0"
                >
                  Unpin
                </button>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <p className="text-sm text-slate-400 text-center mt-6">No messages yet. Start the conversation!</p>
            ) : (
              messages.map((m) => {
                const isMine = m.senderId === currentUserId;
                const isInstructorMsg = m.senderRole === 'instructor' || m.senderRole === 'admin' || m.senderRole === 'superadmin';
                const canModerateThis = info.isManager && !isMine;
                const senderMuted = (info.mutedUserIds || []).includes(m.senderId);

                return (
                  <div key={m._id} className={`group flex gap-2.5 ${isMine ? 'flex-row-reverse' : ''}`}>
                    {m.senderAvatar ? (
                      <img src={m.senderAvatar} alt={m.senderName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">
                        {initialsFor(m.senderName)}
                      </div>
                    )}
                    <div className={`max-w-[70%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                      <div className="flex items-center gap-1.5 mb-0.5 px-1">
                        <span className="text-[11px] font-semibold text-slate-500">{isMine ? 'You' : m.senderName}</span>
                        {isInstructorMsg && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-100">Instructor</span>
                        )}
                        <span className="text-[10px] text-slate-300">{timeAgo(m.createdAt)}</span>
                      </div>
                      <div
                        className={`px-4 py-2.5 rounded-2xl text-sm ${
                          isMine ? 'bg-teal-600 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{m.message}</p>
                      </div>

                      <div className="flex items-center gap-2.5 mt-1 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {(isMine || info.isManager) && (
                          <button onClick={() => handleDelete(m._id)} className="text-[10px] text-slate-400 hover:text-rose-500 cursor-pointer">
                            Remove
                          </button>
                        )}
                        {info.isManager && (
                          <button onClick={() => handlePin(m._id, !m.pinned)} className="text-[10px] text-slate-400 hover:text-amber-500 cursor-pointer">
                            {m.pinned ? 'Unpin' : 'Pin'}
                          </button>
                        )}
                        {canModerateThis && (
                          <button
                            onClick={() => handleMuteToggle(m.senderId, !senderMuted)}
                            className="text-[10px] text-slate-400 hover:text-slate-700 cursor-pointer"
                          >
                            {senderMuted ? 'Unmute' : 'Mute'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={handleSend} className="p-3.5 border-t border-slate-100 flex items-center gap-2">
            <input
              type="text"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={!canPost}
              placeholder={
                isLockedForMe
                  ? 'The instructor has locked this room'
                  : isMutedForMe
                  ? "You've been muted here"
                  : 'Type a message...'
              }
              className="flex-1 px-4 py-2.5 border border-slate-200 rounded-full text-sm focus:outline-none focus:border-teal-500 bg-white disabled:bg-slate-50 disabled:text-slate-400"
            />
            <button
              type="submit"
              disabled={!canPost || !draft.trim() || sending}
              className="w-10 h-10 rounded-full bg-teal-600 hover:bg-teal-700 disabled:opacity-40 text-white flex items-center justify-center flex-shrink-0 cursor-pointer"
            >
              <i className="ri-send-plane-fill" />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
